'use client';

import dynamic from 'next/dynamic';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import FABButtonLocation from '@alltrails/amplitude/enums/FABButtonLocation';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logCustomRouteButtonClicked from '@alltrails/amplitude/events/logCustomRouteButtonClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import IconButton from '@alltrails/denali/components/IconButton';
import DrawPath from '@alltrails/denali/icons/DrawPath';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type Props = {
  trailSlug: string;
  trailId: number;
  isMobileOverviewButton?: boolean;
  isDesktopCta?: boolean;
  analyticsLocation: FABButtonLocation;
  trailPageType: TrailPageType;
};

export default function CustomizeRouteButton({ trailSlug, trailId, analyticsLocation, isMobileOverviewButton, isDesktopCta, trailPageType }: Props) {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const returnTo = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const currentPath = window.location.pathname + window.location.search;
    return encodeURIComponent(currentPath);
  }, []);

  const onCustomizeRouteClick = useCallback(() => {
    logCustomRouteButtonClicked({ location: analyticsLocation, route_loaded: true, trail_id: trailId, trail_page_type: trailPageType });
    if (user) {
      window.location.assign(wrapUrlSafe(`/explore/custom-routes/trail/${trailSlug}${returnTo ? `?returnTo=${returnTo}` : ''}`, languageRegionCode));
    } else {
      setIsSignUpModalOpen(true);
    }
  }, [languageRegionCode, trailSlug, trailId, analyticsLocation, user, returnTo, trailPageType]);

  return (
    <>
      {isMobileOverviewButton ? (
        <Button onClick={onCustomizeRouteClick} text={<FormattedMessage defaultMessage="Customize route" />} width="full" />
      ) : isDesktopCta ? (
        <IconButton
          icon={{ Component: DrawPath }}
          onClick={onCustomizeRouteClick}
          title={intl.formatMessage({ defaultMessage: 'Customize route' })}
        />
      ) : null}
      {isSignUpModalOpen && (
        <SignUpModal
          isOpen
          closeModal={() => setIsSignUpModalOpen(false)}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            window.location.assign(wrapUrlSafe(`/explore/custom-routes/trail/${trailSlug}`, languageRegionCode));
          }}
          trigger="custom routes trail page cta tapped"
          upsellTrigger="custom routes trail page cta tapped"
          hideUpsell
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
        />
      )}
    </>
  );
}
